import ReactGA from 'react-ga';
import { useEffect, useRef } from 'react';
import { isWebComponentFlow } from './commonCode';
import { wcApiUrl } from '../WebComponents/commonComponents/customUtils';

const ANALYTICS_KEY = 'DS_APP_ANALYTICS';
const getAnalyticsState = () => {
  if (!window[ANALYTICS_KEY]) {
    window[ANALYTICS_KEY] = {
      gaInitialized: false,
      gaReady: false,
      gtmInitialized: false,
      reactGaInitialized: false,
    };
  }

  return window[ANALYTICS_KEY];
};

const Analytics = (props) => {
  const baseContext = isWebComponentFlow();
  const { initiateUiResponse, setGoogleCID, environment, wcFlow } = baseContext;
  const gaReadyRef = useRef(false);
  const isCancelledRef = useRef(false);
  const analyticsState = getAnalyticsState();

  const getGATrackingID = () =>
    wcFlow
      ? wcApiUrl(environment).REACT_APP_GA_TRACKING_ID[environment]
      : initiateUiResponse?.REACT_APP_GA_TRACKING_ID;

  const getGTMTrackingID = () =>
    wcFlow
      ? wcApiUrl(environment).REACT_APP_GTM_ID[environment]
      : initiateUiResponse?.REACT_APP_GTM_ID;

  const envCondition = wcFlow
    ? environment === 'dev' || environment === 'prod'
    : initiateUiResponse?.REACT_APP_ENVIRONMENT === 'production' ||
      initiateUiResponse?.REACT_APP_ENVIRONMENT === 'development';

  const initGA = (id) => {
    if (!id || analyticsState.gaInitialized) {
      console.log('[Analytics] GA init skipped', {
        reason: !id ? 'missing-id' : 'already-initialized',
        id,
      });
      return;
    }

    console.log('[Analytics] GA init', {
      id,
      environment,
      wcFlow,
    });

    analyticsState.gaInitialized = true;
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    }
    if (typeof window.gtag !== 'function') {
      window.gtag = gtag;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    script.onload = () => {
      window.gtag('js', new Date());
      window.gtag('config', id);
      analyticsState.gaReady = true;
      gaReadyRef.current = true;
      console.log('[Analytics] GA ready', { id });
    };
    script.onerror = () => {
      console.error('[Analytics] GA script failed', { id });
      analyticsState.gaInitialized = false;
      analyticsState.gaReady = false;
      gaReadyRef.current = false;
    };
    document.head.appendChild(script);
  };

  const initGTM = ({ gtmId, dataLayerName = 'dataLayer' }) => {
    if (!gtmId || analyticsState.gtmInitialized) {
      console.log('[Analytics] GTM init skipped', {
        reason: !gtmId ? 'missing-id' : 'already-initialized',
        gtmId,
      });
      return;
    }

    console.log('[Analytics] GTM init', {
      gtmId,
      environment,
      wcFlow,
    });

    analyticsState.gtmInitialized = true;
    window[dataLayerName] = window[dataLayerName] || [];
    window[dataLayerName].push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}${
      dataLayerName !== 'dataLayer' ? `&l=${dataLayerName}` : ''
    }`;
    script.onerror = () => {
      console.error('[Analytics] GTM script failed', { gtmId });
      analyticsState.gtmInitialized = false;
    };
    document.head.appendChild(script);
  };

  const captureCID = () => {
    let attempts = 0;
    const maxAttempts = 100;

    const getCIDFromCookie = () => {
      const gaCookie = document.cookie.split('; ').find((cookie) => cookie.startsWith('_ga='));

      if (!gaCookie) {
        return null;
      }

      const cookieValue = decodeURIComponent(gaCookie.substring('_ga='.length));
      const parts = cookieValue.split('.');

      if (parts.length < 4) {
        return null;
      }

      return `${parts[2]}.${parts[3]}`;
    };

    const checkCID = () => {
      if (isCancelledRef.current) return;

      attempts += 1;

      const clientId = getCIDFromCookie();

      if (clientId) {
        console.log('[Analytics] CID found', { attempts });
        setGoogleCID(clientId);
        return;
      }

      if (attempts < maxAttempts) {
        setTimeout(checkCID, 150);
        return;
      }
      console.warn('[Analytics] CID not found after max attempts', {
        attempts,
      });
    };
    checkCID();
  };

  useEffect(() => {
    isCancelledRef.current = false;
    console.log('[Analytics] init', {
      environment,
      wcFlow,
      envCondition,
      state: { ...analyticsState },
    });

    if (getGATrackingID()) {
      initGA(getGATrackingID());
    }
    if (envCondition) {
      // Replaced react-gtm-module to avoid its inline GTM bootstrap script, which violates CSP.
      initGTM({
        gtmId: getGTMTrackingID(),
      });

      const tryInitReactGA = () => {
        if (isCancelledRef.current) return;
        if (analyticsState.gaReady) {
          gaReadyRef.current = true;
        }

        if (!gaReadyRef.current) {
          setTimeout(tryInitReactGA, 50);
          return;
        }

        if (!analyticsState.reactGaInitialized) {
          console.log('[Analytics] ReactGA init');
          ReactGA.initialize(getGATrackingID());
          analyticsState.reactGaInitialized = true;
        }
        ReactGA.pageview(window.location.pathname + window.location.search);
      };
      tryInitReactGA();
    }

    captureCID();

    return () => {
      isCancelledRef.current = true;
    };
  }, [initiateUiResponse]);

  return null;
};

export default Analytics;
